import { useMutation, useQuery } from '@apollo/client';
import { Button, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { Pencil, Plus, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  ActiveBadge,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SelectField,
  SettingsPage,
  ShowInactiveToggle,
  TextField,
} from '../../components/settings/SettingsKit.web.jsx';
import {
  CREATE_CUSTOM_FIELD,
  CUSTOM_FIELDS_QUERY,
  SET_CUSTOM_FIELD_ACTIVE,
  UPDATE_CUSTOM_FIELD,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const TYPES = ['TEXT', 'NUMBER', 'DATE', 'BOOLEAN', 'SELECT', 'MULTISELECT'];
const APPLIES_TO = ['BOTH', 'PERSON', 'ORGANIZATION'];
const OPTION_TYPES = ['SELECT', 'MULTISELECT'];

export function CustomFieldsScreen() {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const variables = { includeInactive };
  const { data, loading } = useQuery(CUSTOM_FIELDS_QUERY, { variables });
  const refetch = [{ query: CUSTOM_FIELDS_QUERY, variables }];

  const [create, createState] = useMutation(CREATE_CUSTOM_FIELD, { refetchQueries: refetch });
  const [update, updateState] = useMutation(UPDATE_CUSTOM_FIELD, { refetchQueries: refetch });
  const [setActive, activeState] = useMutation(SET_CUSTOM_FIELD_ACTIVE, {
    refetchQueries: refetch,
  });

  const rows = data?.customFieldDefinitions ?? [];

  return (
    <SettingsPage
      title={S.settings.sections.customFields}
      hint={S.settings.customFields.deactivateHint}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={setIncludeInactive} />
          <Button variant="primary" onClick={() => setEditing('new')} className="gap-2">
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <MutationError error={createState.error || updateState.error || activeState.error} />
      <LoadingOrEmpty loading={loading && rows.length === 0} empty={!loading && rows.length === 0} />

      {rows.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>{S.settings.customFields.label}</TH>
              <TH>{S.settings.customFields.name}</TH>
              <TH>{S.settings.customFields.type}</TH>
              <TH>{S.settings.customFields.appliesTo}</TH>
              <TH>{S.settings.customFields.options}</TH>
              <TH>{S.settings.common.status}</TH>
              <TH>{S.settings.common.actions}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((field) => (
              <TR key={field.id} className={field.isActive ? '' : 'opacity-60'}>
                <TD className="font-medium">{field.labelFa}</TD>
                <TD dir="ltr" className="text-start text-text-muted">
                  {field.name}
                </TD>
                <TD>{S.settings.customFields.types[field.type]}</TD>
                <TD>{S.settings.customFields.appliesToOptions[field.appliesTo]}</TD>
                <TD className="text-text-muted">
                  {field.options.length ? field.options.join('، ') : S.app.none}
                </TD>
                <TD>
                  <ActiveBadge isActive={field.isActive} />
                </TD>
                <TD>
                  <span className="flex items-center gap-1">
                    <Button variant="ghost" title={S.app.edit} onClick={() => setEditing(field)}>
                      <Pencil size={14} aria-hidden="true" />
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(field)}>
                      {field.isActive ? S.app.deactivate : S.app.activate}
                    </Button>
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {editing && (
        <CustomFieldDialog
          field={editing === 'new' ? null : editing}
          busy={createState.loading || updateState.loading}
          onClose={() => setEditing(null)}
          onSubmit={async (input) => {
            if (editing === 'new') await create({ variables: { input } });
            else await update({ variables: { id: editing.id, input } });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? `${S.settings.common.confirmDeactivate(confirming?.labelFa ?? '')} ${S.settings.customFields.deactivateHint}`
            : S.settings.common.confirmActivate(confirming?.labelFa ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() =>
          setActive({ variables: { id: confirming.id, isActive: !confirming.isActive } })
        }
      />
    </SettingsPage>
  );
}

function CustomFieldDialog({ field, onClose, onSubmit, busy }) {
  const isNew = !field;
  const [name, setName] = useState(field?.name ?? '');
  const [labelFa, setLabelFa] = useState(field?.labelFa ?? '');
  const [type, setType] = useState(field?.type ?? 'TEXT');
  const [appliesTo, setAppliesTo] = useState(field?.appliesTo ?? 'BOTH');
  const [options, setOptions] = useState(field?.options ?? []);
  const [draftOption, setDraftOption] = useState('');

  const needsOptions = OPTION_TYPES.includes(type);

  function addOption() {
    const value = draftOption.trim();
    if (!value || options.includes(value)) return;
    setOptions([...options, value]);
    setDraftOption('');
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isNew ? S.settings.customFields.createTitle : `${S.app.edit} — ${field.labelFa}`}
      busy={busy}
      onSubmit={() =>
        onSubmit(
          isNew
            ? { name: name.trim(), labelFa, type, appliesTo, options: needsOptions ? options : [] }
            : { labelFa, appliesTo, options: needsOptions ? options : [] },
        )
      }
    >
      <TextField
        label={S.settings.customFields.name}
        value={name}
        dir="ltr"
        required={isNew}
        disabled={!isNew}
        hint={isNew ? S.settings.customFields.nameHint : S.settings.customFields.nameImmutable}
        onChange={(event) => setName(event.target.value.toLowerCase())}
      />
      <TextField
        label={S.settings.customFields.label}
        value={labelFa}
        required
        onChange={(event) => setLabelFa(event.target.value)}
      />
      <SelectField
        label={S.settings.customFields.type}
        value={type}
        disabled={!isNew}
        hint={isNew ? undefined : S.settings.customFields.nameImmutable}
        onChange={(event) => setType(event.target.value)}
      >
        {TYPES.map((t) => (
          <option key={t} value={t}>
            {S.settings.customFields.types[t]}
          </option>
        ))}
      </SelectField>
      <SelectField
        label={S.settings.customFields.appliesTo}
        value={appliesTo}
        onChange={(event) => setAppliesTo(event.target.value)}
      >
        {APPLIES_TO.map((a) => (
          <option key={a} value={a}>
            {S.settings.customFields.appliesToOptions[a]}
          </option>
        ))}
      </SelectField>

      {needsOptions && (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-text-muted">{S.settings.customFields.options}</span>
          <ul className="flex flex-wrap gap-2">
            {options.map((option) => (
              <li
                key={option}
                className="flex items-center gap-2 rounded border border-border px-3 py-1 text-sm"
              >
                {option}
                <button
                  type="button"
                  aria-label={`${S.app.close} ${option}`}
                  className="text-text-muted hover:text-danger"
                  onClick={() => setOptions(options.filter((o) => o !== option))}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <input
              value={draftOption}
              onChange={(event) => setDraftOption(event.target.value)}
              onKeyDown={(event) => {
                // Enter adds an option instead of submitting the whole form,
                // which is what someone typing a list expects.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addOption();
                }
              }}
              placeholder={S.settings.customFields.addOption}
              className="h-control flex-1 rounded border border-border bg-bg px-4 text-base
                         outline-none focus:border-primary"
            />
            <Button type="button" onClick={addOption}>
              {S.settings.customFields.addOption}
            </Button>
          </div>
          <span className="text-xs text-text-muted">{S.settings.customFields.optionsHint}</span>
        </div>
      )}
    </FormDialog>
  );
}
